import { useMemo } from 'react';
import { useQuery } from 'urql';

import { PAY_V2_QUERY, PositionsEntityKind } from '@revelio/data-access';

import type { ResolvedProfileIds } from './useResolveProfileIds';
import {
  BUCKET_SIZE,
  buildDistributionFromHistogram,
  findUserPercentile,
} from './useSalaryDistribution';

interface UseUserPercentileResult {
  userPercentile: number | null;
  isLoading: boolean;
}

/**
 * Computes the user's percentile within their current role's salary distribution.
 * This "reference percentile" is used by all uplift hooks in percentile mode
 * to find the equivalent salary at the same percentile in target distributions.
 */
export const useUserPercentile = (
  resolvedIds: ResolvedProfileIds,
  userSalary: number
): UseUserPercentileResult => {
  const payV2Variables = useMemo(() => {
    if (!resolvedIds.roleId || userSalary <= 0) return undefined;

    return {
      req: {
        entities: [
          {
            EntID: resolvedIds.roleId,
            EntType: PositionsEntityKind.RoleK150,
          },
        ],
        filters: {
          raw: false,
          ...(resolvedIds.seniorityId
            ? { seniority: [resolvedIds.seniorityId] }
            : {}),
        },
      },
    };
  }, [resolvedIds.roleId, resolvedIds.seniorityId, userSalary]);

  const [{ data, fetching }] = useQuery({
    query: PAY_V2_QUERY,
    variables: payV2Variables,
    pause: !payV2Variables,
  });

  const userPercentile = useMemo((): number | null => {
    if (userSalary <= 0) return null;

    const histogramData = data?.pay_v2?.entities?.[0]?.metrics?.histogram?.base;
    if (!histogramData || histogramData.length === 0) return null;

    const distribution = buildDistributionFromHistogram(
      histogramData,
      BUCKET_SIZE
    );
    if (distribution.length === 0) return null;

    return findUserPercentile(distribution, userSalary);
  }, [data, userSalary]);

  return {
    userPercentile,
    isLoading: fetching,
  };
};
